import { useState } from 'react'
import { Star } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Toast } from '@/components/ui/toast'
import { supabase } from '@/lib/supabase'
import { useAuthStore } from '@/lib/store'

interface ReviewFormProps {
  productId: string
  sellerId: string
  orderId?: string
  onSuccess: () => void
}

export function ReviewForm({ productId, sellerId, orderId, onSuccess }: ReviewFormProps) {
  const user = useAuthStore((state) => state.user)
  const [rating, setRating] = useState(0)
  const [hoverRating, setHoverRating] = useState(0)
  const [comment, setComment] = useState('')
  const [loading, setLoading] = useState(false)
  const [showToast, setShowToast] = useState(false)
  const [toastMessage, setToastMessage] = useState('')
  const [toastType, setToastType] = useState<'success' | 'error'>('success')

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!user) return

    if (rating === 0) {
      setToastMessage('Veuillez sélectionner une note')
      setToastType('error')
      setShowToast(true)
      return
    }

    try {
      setLoading(true)

      // Create product review
      const { error: reviewError } = await supabase
        .from('reviews')
        .insert([
          {
            reviewer_id: user.id,
            rating,
            comment,
            target_type: 'product',
            target_id: productId,
            order_id: orderId,
            metadata: {
              seller_id: sellerId
            }
          }
        ])

      if (reviewError) throw reviewError

      // Update seller rating
      const { data: sellerReviews, error: sellerError } = await supabase
        .from('reviews')
        .select('rating')
        .eq('target_type', 'product')
        .eq('metadata->seller_id', sellerId)

      if (sellerError) throw sellerError

      const averageRating = sellerReviews.reduce((acc, review) => acc + review.rating, 0) / sellerReviews.length

      const { error: updateError } = await supabase
        .from('profiles')
        .update({ rating: averageRating })
        .eq('id', sellerId)

      if (updateError) throw updateError

      setToastMessage('Avis publié avec succès')
      setToastType('success')
      setShowToast(true)
      onSuccess()

      // Reset form
      setRating(0)
      setComment('')
    } catch (err) {
      console.error('Error submitting review:', err)
      setToastMessage('Erreur lors de la publication de l\'avis')
      setToastType('error')
      setShowToast(true)
    } finally {
      setLoading(false)
    }
  }

  return (
    <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-200">
      {showToast && (
        <Toast
          type={toastType}
          title={toastMessage}
          onClose={() => setShowToast(false)}
        />
      )}

      <h3 className="text-lg font-medium text-gray-900 mb-4">
        Donnez votre avis
      </h3>

      <form onSubmit={handleSubmit} className="space-y-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Note
          </label>
          <div className="flex items-center space-x-1">
            {[1, 2, 3, 4, 5].map((value) => (
              <button
                key={value}
                type="button"
                onClick={() => setRating(value)}
                onMouseEnter={() => setHoverRating(value)}
                onMouseLeave={() => setHoverRating(0)}
                className="p-1 focus:outline-none focus:ring-2 focus:ring-primary rounded-full transition-transform hover:scale-110"
              >
                <Star
                  className={`h-8 w-8 ${
                    value <= (hoverRating || rating)
                      ? 'text-yellow-400 fill-current'
                      : 'text-gray-300'
                  }`}
                />
              </button>
            ))}
          </div>
        </div>

        <div>
          <label htmlFor="comment" className="block text-sm font-medium text-gray-700 mb-2">
            Commentaire
          </label>
          <textarea
            id="comment"
            rows={4}
            value={comment}
            onChange={(e) => setComment(e.target.value)}
            className="block w-full border-gray-300 rounded-md shadow-sm focus:ring-primary focus:border-primary sm:text-sm"
            placeholder="Partagez votre expérience avec ce produit..."
          />
        </div>

        <Button
          type="submit"
          variant="primary"
          className="w-full shadow-sm hover:shadow-md transition-shadow"
          disabled={loading || rating === 0}
        >
          {loading ? (
            <div className="w-6 h-6 border-2 border-white border-t-transparent rounded-full animate-spin" />
          ) : (
            'Publier l\'avis'
          )}
        </Button>
      </form>
    </div>
  )
}